import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Download, FileSpreadsheet, BarChart3, AlertTriangle, MapPin, Package } from "lucide-react";
import api from "../lib/api";
import { formatRupiah, formatCompactRupiah, formatNumber, formatDate, statusClass } from "../lib/format";
import { Section, Skeleton, EmptyState, StatCard, Pagination, usePagination } from "../components/UI";

export default function LaporanPage() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [sppgUserId, setSppgUserId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [sppgUsers, setSppgUsers] = useState([]);
  const [dateError, setDateError] = useState("");

  const showDateError = (msg) => {
    setDateError(msg);
    setTimeout(() => setDateError(""), 4000);
  };

  const handleDateStart = (val) => {
    if (val && end && val > end) {
      showDateError("Tanggal awal tidak boleh melebihi tanggal akhir");
      setStart("");
      return;
    }
    setDateError("");
    setStart(val);
  };

  const handleDateEnd = (val) => {
    if (val && start && val < start) {
      showDateError("Tanggal akhir tidak boleh kurang dari tanggal awal");
      setEnd("");
      return;
    }
    setDateError("");
    setEnd(val);
  };

  // Fetch supplier list and SPPG user list on mount
  useEffect(() => {
    (async () => {
      try {
        const [supRes, usersRes] = await Promise.all([
          api.get("/suppliers"),
          api.get("/users", { params: { role: "SPPG" } }),
        ]);
        setSuppliers(supRes.data || []);
        setSppgUsers(usersRes.data || []);
      } catch {
        // SPPG users endpoint may fail for non-KPPG; fallback to empty
      }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = new Date(new Date(end).getTime() + 24 * 3600 * 1000 - 1).toISOString();
      if (supplierId) params.supplier_id = supplierId;
      if (sppgUserId) params.sppg_user_id = sppgUserId;
      const { data } = await api.get("/reports/transactions", { params });
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportCsv = async () => {
    const params = new URLSearchParams({ format: "csv" });
    if (start) params.set("start", start);
    if (end) params.set("end", new Date(new Date(end).getTime() + 24 * 3600 * 1000 - 1).toISOString());
    if (supplierId) params.set("supplier_id", supplierId);
    if (sppgUserId) params.set("sppg_user_id", sppgUserId);
    const url = `${api.defaults.baseURL}/reports/transactions?${params.toString()}`;
    const token = localStorage.getItem("kedan_token");
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `laporan-transaksi-${Date.now()}.csv`;
    link.click();
  };

  const exportPdf = () => {
    window.print();
  };

  // Extract unique SPPG names from data as fallback when /users is not available
  const sppgOptions = useMemo(() => {
    if (sppgUsers.length > 0) {
      return sppgUsers.map((u) => ({ id: u.id, name: u.name }));
    }
    if (!data?.items) return [];
    const map = new Map();
    data.items.forEach((r) => {
      if (r.sppg_user_id && !map.has(r.sppg_user_id)) {
        map.set(r.sppg_user_id, r.sppg_name);
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [sppgUsers, data]);

  const resetFilters = () => {
    setStart("");
    setEnd("");
    setSupplierId("");
    setSppgUserId("");
    setTimeout(load, 50);
  };

  return (
    <div className="space-y-6" data-testid="laporan-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="kedan-label text-slate-500">Analisis</div>
          <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">Laporan & Analisis</h1>
          <p className="text-slate-500 text-sm mt-1">Rekap transaksi & ekspor data periode tertentu.</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <button onClick={exportCsv} className="kedan-btn-secondary" data-testid="export-csv">
            <FileSpreadsheet className="w-4 h-4" /> Ekspor CSV
          </button>
          <button onClick={exportPdf} className="kedan-btn-primary" data-testid="export-pdf">
            <Download className="w-4 h-4" /> Ekspor PDF
          </button>
        </div>
      </header>

      <div className="kedan-card p-5 flex items-end gap-3 flex-wrap print:hidden">
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Dari Tanggal</label>
          <input type="date" className={`kedan-input !w-44 ${dateError ? "!border-red-300" : ""}`} value={start} onChange={(e) => handleDateStart(e.target.value)} data-testid="filter-start" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Sampai Tanggal</label>
          <input type="date" className={`kedan-input !w-44 ${dateError ? "!border-red-300" : ""}`} value={end} onChange={(e) => handleDateEnd(e.target.value)} data-testid="filter-end" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Supplier</label>
          <select className="kedan-input !w-52" value={supplierId} onChange={(e) => setSupplierId(e.target.value)} data-testid="filter-supplier">
            <option value="">Semua Supplier</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>{s.nama_usaha}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">SPPG</label>
          <select className="kedan-input !w-52" value={sppgUserId} onChange={(e) => setSppgUserId(e.target.value)} data-testid="filter-sppg">
            <option value="">Semua SPPG</option>
            {sppgOptions.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>
        <button onClick={load} className="kedan-btn-primary" data-testid="apply-filter">Terapkan Filter</button>
        <button onClick={resetFilters} className="kedan-btn-secondary">Reset</button>
      </div>

      {/* Date validation notification */}
      {dateError && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs font-medium animate-[fadeIn_0.2s_ease-out] print:hidden" data-testid="date-error">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {dateError}
        </div>
      )}

      {/* Cakupan wilayah Pemda — diambil dari kota pada Profil Pemda */}
      {!loading && data && "scope_kota" in data.summary && (
        data.summary.scope_kota ? (
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-xs font-medium print:hidden"
            data-testid="rep-scope-kota">
            <MapPin className="w-4 h-4 shrink-0" />
            Menampilkan transaksi wilayah <strong>{data.summary.scope_kota}</strong> — supplier berdomisili di kota tersebut atau tujuan pengiriman ke kota tersebut.
          </div>
        ) : (
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium print:hidden"
            data-testid="rep-scope-empty">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Wilayah kerja belum ditentukan. Lengkapi <Link to="/profil-pemda" className="underline font-semibold">Profil Pemda</Link> dan pilih Kabupaten/Kota agar data transaksi wilayah Anda tampil.
          </div>
        )
      )}

      {loading ? (
        <Skeleton className="h-64" />
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 print:hidden">
            <StatCard icon={BarChart3} label="Total PO" value={data.summary.total_po} accent="blue" testid="rep-total-po" />
            <StatCard icon={BarChart3} label="Total Nilai" value={formatCompactRupiah(data.summary.total_nilai)} accent="emerald" testid="rep-total-nilai" />
            <StatCard icon={BarChart3} label="Selesai" value={data.summary.by_status?.Selesai || 0} accent="emerald" testid="rep-selesai" />
            <StatCard icon={BarChart3} label="Aktif" value={
              (data.summary.by_status?.["Draft PO"] || 0) +
              (data.summary.by_status?.["PO Terkirim"] || 0) +
              (data.summary.by_status?.["PO Diproses"] || 0) +
              (data.summary.by_status?.["Dalam Pengiriman"] || 0) +
              (data.summary.by_status?.["Barang Diterima"] || 0)
            } accent="amber" testid="rep-aktif" />
            <StatCard icon={BarChart3} label="Dibatalkan" value={data.summary.by_status?.Dibatalkan || 0} accent="rose" testid="rep-dibatalkan" />
          </div>

          <Section
            title="Akumulasi Produk Dipesan"
            subtitle={`${(data.summary.by_product || []).length} produk · tidak termasuk draft dan PO dibatalkan`}
          >
            {(data.summary.by_product || []).length === 0 ? (
              <EmptyState title="Belum ada produk yang dipesan" description="Belum ada PO berjalan pada periode ini." icon={Package} />
            ) : (
              <ProdukTable rows={data.summary.by_product} />
            )}
          </Section>

          <Section title="Detail Transaksi" subtitle={`${data.items.length} entri`}>
            {data.items.length === 0 ? (
              <EmptyState title="Tidak ada transaksi pada periode ini" />
            ) : (
              <LaporanTable items={data.items} />
            )}
          </Section>
        </>
      )}
    </div>
  );
}

function ProdukTable({ rows }) {
  const maks = Math.max(...rows.map((r) => r.qty), 1);
  const totalQty = rows.reduce((a, r) => a + r.qty, 0);
  const totalNilai = rows.reduce((a, r) => a + r.nilai, 0);

  return (
    <div className="kedan-card overflow-hidden" data-testid="laporan-produk">
      <div className="max-h-[420px] overflow-y-auto kedan-scrollbar print:max-h-none print:overflow-visible">
        <table className="kedan-table">
          <thead>
            <tr>
              <th className="w-10">#</th>
              <th>Nama Produk</th>
              <th className="text-right">Total Qty</th>
              <th className="print:hidden">Porsi</th>
              <th className="text-right">Total Nilai</th>
              <th className="text-right">Jumlah PO</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.nama}>
                <td className="text-slate-400 text-xs">{i + 1}</td>
                <td className="font-medium">{r.nama}</td>
                <td className="text-right font-mono font-semibold">{formatNumber(r.qty)}</td>
                <td className="print:hidden">
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden min-w-[80px]">
                    <div
                      className="h-full bg-[#045498] rounded-full"
                      style={{ width: `${Math.max(4, (r.qty / maks) * 100)}%` }}
                    />
                  </div>
                </td>
                <td className="text-right font-mono" title={formatRupiah(r.nilai)}>{formatCompactRupiah(r.nilai)}</td>
                <td className="text-right font-mono text-slate-600">{formatNumber(r.po)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50 font-semibold">
              <td colSpan={2} className="text-right text-xs uppercase tracking-wider text-slate-500">Total</td>
              <td className="text-right font-mono">{formatNumber(totalQty)}</td>
              <td className="print:hidden" />
              <td className="text-right font-mono" title={formatRupiah(totalNilai)}>{formatCompactRupiah(totalNilai)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function LaporanTable({ items }) {
  const { page, setPage, perPage, changePerPage, totalPages, paged, totalItems } = usePagination(items, 10);

  return (
    <div className="kedan-card overflow-hidden">
      {/* Screen View (Paginated) */}
      <table className="kedan-table print:hidden">
        <thead><tr>
          <th>No. PO</th><th>Tanggal</th><th>SPPG</th><th>Supplier</th>
          <th>Status</th><th className="text-right">Total</th>
        </tr></thead>
        <tbody>
          {paged.map((r) => (
            <tr key={r.id}>
              <td className="font-mono text-xs">
                <Link to={`/purchase-orders/${r.id}`} className="text-[#045498] hover:underline">
                  {r.po_number}
                </Link>
              </td>
              <td>{formatDate(r.created_at)}</td>
              <td>{r.sppg_name}</td>
              <td>{r.supplier_nama}</td>
              <td><span className={`kedan-badge ${statusClass(r.status)}`}>{r.status}</span></td>
              <td className="text-right font-mono" title={formatRupiah(r.total)}>{formatCompactRupiah(r.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Print View (All Items) */}
      <table className="kedan-table hidden print:table w-full">
        <thead><tr>
          <th>No. PO</th><th>Tanggal</th><th>SPPG</th><th>Supplier</th>
          <th>Status</th><th className="text-right">Total</th>
        </tr></thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id}>
              <td className="font-mono text-xs">{r.po_number}</td>
              <td>{formatDate(r.created_at)}</td>
              <td>{r.sppg_name}</td>
              <td>{r.supplier_nama}</td>
              <td><span className={`kedan-badge ${statusClass(r.status)}`}>{r.status}</span></td>
              <td className="text-right font-mono">{formatRupiah(r.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="print:hidden">
        <Pagination page={page} totalPages={totalPages} totalItems={totalItems} perPage={perPage} onPageChange={setPage} onPerPageChange={changePerPage} />
      </div>
    </div>
  );
}
